"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.interpolateEmailTemplate = exports.interpolateTemplate = void 0;
const _ = require("lodash");
const moment = require("moment");
const contact_1 = require("../models/contact");
const person_1 = require("../models/person");
const string_format_utils_1 = require("./string-format-utils");
const higherEducationLevels = ['Undergrad', 'Master', 'Phd'];
const capitalize = (input) => (input ? _.startCase(input.toLowerCase()) : input);
const preprocessTemplateForHtmlVariables = (template, context) => {
    const normalizeVariableName = (variableName) => {
        // Remove braces, spaces, and convert to lowercase for comparison
        return variableName.replace(/[{}\s]/g, '').toLowerCase();
    };
    const isHtmlContent = (value) => {
        if (typeof value !== 'string') {
            return false;
        }
        // Check if the string contains block-level HTML tags
        return /<div[^>]*>/i.test(value) || /<p[^>]*>/i.test(value);
    };
    // Create a normalized context map for fast lookups
    const normalizedContext = {};
    Object.keys(context).forEach((key) => {
        normalizedContext[normalizeVariableName(key)] = context[key];
    });
    // Find all div-wrapped variables and process each one individually
    const divWrappedVariablePattern = /<div>\s*(\{\{[^}]+\}\})\s*<\/div>/gi;
    return template.replace(divWrappedVariablePattern, (match, variableRef) => {
        // Extract and normalize the variable name from {{variableName}}
        const normalizedVarName = normalizeVariableName(variableRef);
        // Look up the value in the normalized context
        const value = normalizedContext[normalizedVarName];
        // Only unwrap if this specific variable's value contains HTML
        if (isHtmlContent(value)) {
            return variableRef; // Return just {{variableName}} without the div wrapper
        }
        // Keep the div wrapper for non-HTML values
        return match;
    });
};
const interpolateTemplate = (template, context) => {
    const preprocessedTemplate = preprocessTemplateForHtmlVariables(template, context);
    return _.template(preprocessedTemplate, { interpolate: /{{([\s\S]+?)}}/g, evaluate: /\[\%([\s\S]+?)\%\]/g })(context);
};
exports.interpolateTemplate = interpolateTemplate;
const interpolateEmailTemplate = (template, data) => {
    var _a, _b, _c, _d, _e, _f;
    const context = {
        atsLink: null,
        awaitingClientFeedbackCandidates: [],
        candidateFirstName: null,
        clientFinalRoundCandidates: [],
        clientFirstRoundCandidates: [],
        clientIntroPerson: null,
        clientIntroPersonNameWithLink: null,
        clientIntroPersonNameAndRoleWithLink: null,
        clientName: null,
        hasOnePager: false,
        onePagerText: null,
        hiringManagerFirstNames: null,
        hiringManagersNames: null,
        candidateContactDetails: null,
        jobTitle: null,
        personFirstName: null,
        personFullName: null,
        personLinkedin: null,
        personPublicLinkedin: null,
        personRecruiterLinkedin: null,
        phoneCallSchedulingLink: null,
        phoneCallSchedulingLinkText: null,
        phoneNumberConfirmationText: null,
        primaryEmail: null,
        primaryPhone: null,
        profileDetails: {
            allEducations: [],
            currentPositions: [],
            higherEducations: [],
            latestDisciplineData: null,
            latestPosition: null,
            mostRecentPastPositionWithJobDiscipline: null,
            pastPositions: [],
            submissionEmailPositions: []
        },
        recruitingActivity: null,
        salary: null,
        schedulingLink: null,
        senderFirstName: null,
        senderFullName: null,
        senderSignature: null,
        submissionMethod: null,
        visaStatus: null,
        candidateNameWithLink: null,
        candidateExperiences: null,
        candidateEducations: null
    };
    const { awaitingClientFeedbackCandidates, client, clientFinalRoundCandidates, clientFirstRoundCandidates, job, person, personContacts, profile, profileUrls, senderAccount, senderCalendarLink, senderName, submissionMethod, user } = data;
    if (senderAccount) {
        context.senderFirstName = capitalize(senderAccount.name.first);
        context.senderSignature = capitalize(senderAccount.name.first);
        context.senderFullName = capitalize(senderAccount.name.full);
        context.schedulingLink = senderCalendarLink;
    }
    if (senderName) {
        context.senderFirstName = capitalize(senderName.first);
        context.senderSignature = capitalize(senderName.first);
        context.senderFullName = capitalize(senderName.full);
    }
    if (user && user.name.full === ((_a = senderAccount === null || senderAccount === void 0 ? void 0 : senderAccount.name) === null || _a === void 0 ? void 0 : _a.full)) {
        context.senderSignature = (_b = user.signature) !== null && _b !== void 0 ? _b : context.senderSignature;
        if (user.calendarLink) {
            context.phoneCallSchedulingLink = `${user.calendarLink}?utm_medium=${client.name}&utm_campaign=${job.id}&utm_source=${person.id}`;
            context.phoneCallSchedulingLinkText = user.calendarLink;
        }
    }
    if (!context.phoneCallSchedulingLink) {
        context.phoneCallSchedulingLinkText = '{insert scheduling link here from toolbar button}';
    }
    if (submissionMethod) {
        context.submissionMethod = submissionMethod;
    }
    if (client) {
        context.clientName = client.name;
    }
    if (job) {
        context.jobTitle = job.title;
        context.hasOnePager = !!job.onePager;
        if (context.hasOnePager) {
            context.onePagerText = `<div>I'm also attaching the one pager here, thought it would be useful to keep
            everything in one place as we start the process with ${context.clientName}.<br><br></div> `;
        }
        context.atsLink = (0, string_format_utils_1.formatUrl)(job.submissionSettings.atsLink);
    }
    if (client && job) {
        const hiringManagerEmails = job.submissionSettings.emailTo;
        const introEmailContact = job.introSettings.emailTo;
        const { hiringManagers } = client;
        context.hiringManagerFirstNames = hiringManagerEmails.map((email) => hiringManagers.find((h) => h.email === email).name.first);
        const introContact = hiringManagers.find((h) => h.email === introEmailContact);
        context.clientIntroPerson = {
            firstName: introContact ? capitalize(introContact.name.first) : null,
            fullName: introContact ? capitalize(introContact.name.full) : null,
            linkedin: introContact ? (0, string_format_utils_1.formatUrl)(introContact.linkedIn) : null,
            role: introContact ? introContact.role : null
        };
        context.clientIntroPersonNameWithLink = (_d = (_c = context.clientIntroPerson) === null || _c === void 0 ? void 0 : _c.firstName) !== null && _d !== void 0 ? _d : '';
        if (context.clientIntroPerson.linkedin) {
            context.clientIntroPersonNameWithLink += ` (<a href="${context.clientIntroPerson.linkedin}">${context.clientIntroPerson.linkedin}</a>)`;
        }
        context.clientIntroPersonNameAndRoleWithLink = context.clientIntroPersonNameWithLink;
        if (context.clientIntroPerson.role) {
            context.clientIntroPerson.role;
        }
        context.clientIntroPersonNameWithLink += ` at ` + context.clientName;
        context.clientIntroPersonNameAndRoleWithLink += ' at ' + context.clientName;
    }
    if (profile) {
        const { fullName, educations, firstName, meta, positions } = profile.content;
        context.personFullName = capitalize(fullName);
        context.personFirstName = capitalize(firstName);
        context.candidateFirstName = context.personFirstName;
        const allEducations = educations.map((e) => ({
            degree: e.degree,
            fieldOfStudy: e.fieldOfStudy,
            schoolName: e.schoolName
        }));
        const higherEducations = educations
            .filter((education) => education.meta &&
            education.meta.degreeInfo.length > 0 &&
            !_.isEmpty(_.intersection(education.meta.degreeInfo.map((d) => d.mappedDegreeType), higherEducationLevels)))
            .map((e) => ({
            degree: e.degree,
            fieldOfStudy: e.fieldOfStudy,
            schoolName: e.schoolName
        }));
        context.profileDetails.allEducations = allEducations;
        context.profileDetails.higherEducations = higherEducations;
        const { experience } = meta;
        const latestDisciplineData = experience.latestJobDiscipline === 'N/A'
            ? null
            : experience.totalExperienceByJobDiscipline.find((d) => d.discipline === experience.latestJobDiscipline);
        const latestPosition = positions.find((p) => p.meta.latest && experience.latestCompanyName === p.companyName);
        context.profileDetails.latestPosition = {
            companyName: latestPosition ? latestPosition.companyName : null,
            startYear: latestPosition && latestPosition.startDate ? moment(latestPosition.startDate).year().toString() : null,
            title: latestPosition ? latestPosition.title : null
        };
        context.profileDetails.latestDisciplineData = {
            discipline: latestDisciplineData ? latestDisciplineData.discipline : null,
            yearsOfExperience: latestDisciplineData ? roundToHalfYear(latestDisciplineData.years).toString() : null
        };
        const currentPositions = positions.filter((p) => p.meta.latest);
        const pastPositions = positions.filter((p) => !p.meta.latest && !currentPositions.find((c) => c.companyName === p.companyName));
        context.profileDetails.currentPositions = currentPositions.map((p) => ({
            companyName: p.companyName,
            companyUrl: p.companyUrl,
            startYear: p.startDate ? moment(p.startDate).year().toString() : null,
            title: p.title
        }));
        context.profileDetails.pastPositions = pastPositions.map((p) => ({
            companyName: p.companyName,
            companyUrl: p.companyUrl,
            startYear: p.startDate ? moment(p.startDate).year().toString() : null,
            title: p.title
        }));
        context.profileDetails.submissionEmailPositions = currentPositions
            .map((p) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            return ({
                companyName: p.companyName,
                companyUrl: p.companyUrl,
                startYear: p.startDate ? moment(p.startDate).year().toString() : null,
                title: 'Currently ' + p.title,
                cbShortDescription: (_b = (_a = p.meta) === null || _a === void 0 ? void 0 : _a.rocketCompanyData) === null || _b === void 0 ? void 0 : _b.cbShortDescription,
                cbFoundedOn: (_d = (_c = p.meta) === null || _c === void 0 ? void 0 : _c.rocketCompanyData) === null || _d === void 0 ? void 0 : _d.cbFoundedOn,
                cbFundingStatus: (_f = (_e = p.meta) === null || _e === void 0 ? void 0 : _e.rocketCompanyData) === null || _f === void 0 ? void 0 : _f.cbFundingStatus,
                cbTotalFundingUSD: ((_h = (_g = p.meta) === null || _g === void 0 ? void 0 : _g.rocketCompanyData) === null || _h === void 0 ? void 0 : _h.cbTotalFundingUSD) &&
                    Number((_k = (_j = p.meta) === null || _j === void 0 ? void 0 : _j.rocketCompanyData) === null || _k === void 0 ? void 0 : _k.cbTotalFundingUSD) < 1000000000
                    ? (0, string_format_utils_1.currencyFormat)(Number((_m = (_l = p.meta) === null || _l === void 0 ? void 0 : _l.rocketCompanyData) === null || _m === void 0 ? void 0 : _m.cbTotalFundingUSD), 0)
                    : null,
                isCompanyPublic: p.companyStatus === 'public',
                companyIn500ListByMarketCap: p.companyMarketCap > 14600000000,
                companyIPOYear: p.companyIPOYear,
                companyStockSymbolLink: p.companyStockSymbolLink
            });
        })
            .concat(pastPositions.map((p) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
            return ({
                companyName: p.companyName,
                companyUrl: p.companyUrl,
                startYear: p.startDate ? moment(p.startDate).year().toString() : null,
                title: 'Previously ' + p.title,
                cbShortDescription: (_b = (_a = p.meta) === null || _a === void 0 ? void 0 : _a.rocketCompanyData) === null || _b === void 0 ? void 0 : _b.cbShortDescription,
                cbFoundedOn: (_d = (_c = p.meta) === null || _c === void 0 ? void 0 : _c.rocketCompanyData) === null || _d === void 0 ? void 0 : _d.cbFoundedOn,
                cbFundingStatus: (_f = (_e = p.meta) === null || _e === void 0 ? void 0 : _e.rocketCompanyData) === null || _f === void 0 ? void 0 : _f.cbFundingStatus,
                cbTotalFundingUSD: ((_h = (_g = p.meta) === null || _g === void 0 ? void 0 : _g.rocketCompanyData) === null || _h === void 0 ? void 0 : _h.cbTotalFundingUSD) &&
                    Number((_k = (_j = p.meta) === null || _j === void 0 ? void 0 : _j.rocketCompanyData) === null || _k === void 0 ? void 0 : _k.cbTotalFundingUSD) < 1000000000
                    ? (0, string_format_utils_1.currencyFormat)(Number((_m = (_l = p.meta) === null || _l === void 0 ? void 0 : _l.rocketCompanyData) === null || _m === void 0 ? void 0 : _m.cbTotalFundingUSD), 0)
                    : null,
                isCompanyPublic: p.companyStatus === 'public',
                companyIn500ListByMarketCap: p.companyMarketCap > 14600000000,
                companyIPOYear: p.companyIPOYear,
                companyStockSymbolLink: p.companyStockSymbolLink
            });
        }))
            .slice(0, 2)
            .map((p, index, self) => {
            const first = index === self.findIndex((t) => { var _a, _b; return ((_a = t.companyName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === ((_b = p.companyName) === null || _b === void 0 ? void 0 : _b.toLowerCase()); });
            const cbShortDescription = p.cbShortDescription;
            const cbTotalFundingUSD = p.cbTotalFundingUSD;
            return Object.assign(Object.assign({}, p), { cbShortDescription: first ? cbShortDescription : '', cbTotalFundingUSD: first ? cbTotalFundingUSD : '' });
        });
    }
    if (profile && job) {
        const { positions } = profile.content;
        const mostRecentPastPositionWithJobDiscipline = positions.find((p) => !p.meta.latest && (job.discipline === 'N/A' || p.meta.rocketDiscipline === job.discipline));
        context.profileDetails.mostRecentPastPositionWithJobDiscipline = {
            companyName: mostRecentPastPositionWithJobDiscipline
                ? mostRecentPastPositionWithJobDiscipline.companyName
                : null,
            startYear: mostRecentPastPositionWithJobDiscipline && mostRecentPastPositionWithJobDiscipline.startDate
                ? moment(mostRecentPastPositionWithJobDiscipline.startDate).year().toString()
                : null,
            title: mostRecentPastPositionWithJobDiscipline ? mostRecentPastPositionWithJobDiscipline.title : null
        };
    }
    if (profileUrls) {
        const recruiterLinkedin = profileUrls
            .map((p) => p.url)
            .find((link) => _.includes(link, 'linkedin') && !_.includes(link, '/recruiter/'));
        context.personRecruiterLinkedin = (0, string_format_utils_1.formatUrl)(recruiterLinkedin);
        const publicLinkedin = profileUrls.map((p) => p.url).find((p) => p.match(/www.linkedin.com\/in/));
        context.personPublicLinkedin = (0, string_format_utils_1.formatUrl)(publicLinkedin);
        context.personLinkedin = context.personPublicLinkedin || context.personRecruiterLinkedin;
    }
    if (person) {
        context.personFullName = person.name.full;
        context.personFirstName = person.name.first;
        context.candidateFirstName = context.personFirstName;
        context.hiringManagersNames =
            ((_e = context.hiringManagerFirstNames) === null || _e === void 0 ? void 0 : _e.length) > 0
                ? context.hiringManagerFirstNames.join('/')
                : '{hiringManagerFirstNames}';
        context.candidateNameWithLink = context.personFullName;
        if (context.personPublicLinkedin) {
            context.candidateNameWithLink += ` (<a href="${context.personPublicLinkedin}">${context.personPublicLinkedin}</a>)`;
        }
        if (context.profileDetails.submissionEmailPositions.length > 0) {
            let experiencesList = '';
            for (const position of context.profileDetails.submissionEmailPositions) {
                const company = position.companyUrl
                    ? '<a href="' + position.companyUrl + '" target="_blank">' + position.companyName + '</a>'
                    : position.companyName;
                let companyDetails = '';
                const cbFoundedOn = position.cbFoundedOn ? new Date(position.cbFoundedOn).getFullYear() : null;
                let cbFundingStatus = '';
                if (position.cbFundingStatus) {
                    switch (position.cbFundingStatus) {
                        case 'seed':
                        case 'angel':
                        case 'grant':
                        case 'pre_seed':
                            cbFundingStatus = 'Seed';
                            break;
                        case 'series_a':
                            cbFundingStatus = 'Series A';
                            break;
                        case 'series_b':
                            cbFundingStatus = 'Series B';
                            break;
                        case 'series_c':
                            cbFundingStatus = 'Series C';
                            break;
                        case 'series_d':
                            cbFundingStatus = 'Series D';
                            break;
                        case 'series_e':
                            cbFundingStatus = 'Series E';
                            break;
                        case 'series_f':
                            cbFundingStatus = 'Series F';
                            break;
                        case 'series_g':
                            cbFundingStatus = 'Series G';
                            break;
                        case 'series_h':
                            cbFundingStatus = 'Series H';
                            break;
                        case 'series_i':
                            cbFundingStatus = 'Series I';
                            break;
                        case 'series_j':
                            cbFundingStatus = 'Series J';
                            break;
                        case 'post_ipo_debt':
                        case 'post_ipo_secondary':
                        case 'post_ipo_equity':
                            cbFundingStatus = 'Public company';
                            break;
                        case 'private_equity':
                            cbFundingStatus = 'Private Equity';
                            break;
                        case 'series_unknown':
                            cbFundingStatus = 'Venture backed';
                            break;
                        case 'corporate_round':
                            cbFundingStatus = 'Corporate Round';
                            break;
                        case 'convertible_note':
                            cbFundingStatus = 'Convertible Note';
                            break;
                        case 'equity_crowdfunding':
                        case 'product_crowdfunding':
                            cbFundingStatus = 'Crowdfunded';
                            break;
                        case 'initial_coin_offering':
                            cbFundingStatus = 'Initial Coin Offering';
                            break;
                        case 'undisclosed':
                        case 'debt_financing':
                        case 'secondary_market':
                        case 'non_equity_assistance':
                            break;
                        default:
                            break;
                    }
                }
                const cbFoundedOnAndFundingStatus = cbFoundedOn > 2000
                    ? cbFoundedOn && cbFundingStatus
                        ? ' (' + cbFundingStatus + ', founded ' + cbFoundedOn + ')'
                        : cbFoundedOn
                            ? ' (Founded ' + cbFoundedOn + ')'
                            : cbFundingStatus
                                ? ' (' + cbFundingStatus + ')'
                                : ''
                    : '';
                if (!position.companyIn500ListByMarketCap) {
                    companyDetails += `<ul>`;
                    companyDetails += position.cbShortDescription ? '<li>' + position.cbShortDescription + '</li>' : '';
                    companyDetails += position.isCompanyPublic
                        ? position.companyStockSymbolLink && position.companyIPOYear
                            ? ' <li><a href="' +
                                position.companyStockSymbolLink +
                                '" target="_blank">Public company</a> - IPO in ' +
                                position.companyIPOYear +
                                '</li>'
                            : '<li>Public company</li>'
                        : cbFoundedOn > 2000
                            ? position.cbTotalFundingUSD
                                ? ((_f = '<li>Raised ' + position.cbTotalFundingUSD + cbFoundedOnAndFundingStatus) !== null && _f !== void 0 ? _f : '' + '</li>')
                                : ''
                            : '';
                    companyDetails += ` </ul>`;
                }
                experiencesList += `<li>
                    ${position.title} at ${company}
                    ${companyDetails}
                </li>`;
            }
            context.candidateExperiences = `<div><strong>Experience</strong></div><ul>${experiencesList}</ul>`;
        }
        if (context.profileDetails.higherEducations.length > 0) {
            let educationList = '';
            for (const edu of context.profileDetails.allEducations) {
                if (edu.fieldOfStudy) {
                    if (edu.degree) {
                        educationList += `<li>${edu.degree} in ${edu.fieldOfStudy} at ${edu.schoolName}</li>`;
                    }
                    else {
                        educationList += `<li>Studied ${edu.fieldOfStudy} at ${edu.schoolName}</li>`;
                    }
                }
            }
            context.candidateEducations = `<div><strong>Education</strong></div><ul>${educationList}</ul>`;
        }
        let visaStatus;
        if (person.visaStatus) {
            visaStatus = person.visaStatusSource === 'user_input' ? person_1.personVisaSubmissionText[person.visaStatus] : null;
        }
        context.visaStatus = visaStatus;
    }
    if (personContacts) {
        const primaryEmail = personContacts.find((c) => c.primary && !c.invalid && c.channel === contact_1.ContactChannel.Email);
        const primaryPhone = personContacts.find((c) => c.primary && !c.invalid && c.channel === contact_1.ContactChannel.Phone);
        context.primaryEmail = primaryEmail ? primaryEmail.value : null;
        context.primaryPhone = primaryPhone ? primaryPhone.value : null;
        let contactDetailsContent = '';
        if (context.primaryEmail) {
            contactDetailsContent += `<div>
                Email: <a href="mailto:${context.primaryEmail}">${context.primaryEmail}</a>
            </div>`;
        }
        else {
            contactDetailsContent += `<div>{email}</div>`;
        }
        if (context.primaryPhone) {
            contactDetailsContent += `<div>Phone: <a href="tel:${context.primaryPhone}">${context.primaryPhone}</a></div>`;
            context.phoneNumberConfirmationText = `Also, can you please confirm ${context.primaryPhone} is still the best number to get in touch with you?`;
        }
        else {
            context.phoneNumberConfirmationText = `Also, can you please share the best phone number to get in touch with you?`;
        }
        if (context.personLinkedin) {
            contactDetailsContent += `<div>Linkedin: <a href="https://${context.personLinkedin}">${context.personLinkedin}</a></div>`;
        }
        context.candidateContactDetails = `<div><strong>Contact Details</strong></div><br>${contactDetailsContent}`;
    }
    if (person && job) {
        context.salary = getPersonSalaryInfo(person, job.id);
        context.recruitingActivity = getPersonRecruitingActivityInfo(person, job.id);
    }
    if (awaitingClientFeedbackCandidates) {
        context.awaitingClientFeedbackCandidates = awaitingClientFeedbackCandidates.map((c) => ({
            link: (0, string_format_utils_1.formatUrl)(c.link),
            name: c.name.full,
            submittedDate: moment(c.clientSubmittedAt).format('M/D')
        }));
    }
    if (clientFirstRoundCandidates) {
        context.clientFirstRoundCandidates = clientFirstRoundCandidates.map((c) => ({
            link: (0, string_format_utils_1.formatUrl)(c.link),
            name: c.name.full,
            submittedDate: moment(c.clientSubmittedAt).format('M/D')
        }));
    }
    if (clientFinalRoundCandidates) {
        context.clientFinalRoundCandidates = clientFinalRoundCandidates.map((c) => ({
            link: (0, string_format_utils_1.formatUrl)(c.link),
            name: c.name.full,
            submittedDate: moment(c.clientSubmittedAt).format('M/D')
        }));
    }
    return (0, exports.interpolateTemplate)(template, context);
};
exports.interpolateEmailTemplate = interpolateEmailTemplate;
const salaryKinds = {
    accepted: {
        label: 'will accept',
        order: 1
    },
    desired: {
        label: 'desires',
        order: 2
    },
    volunteered: {
        label: 'volunteered they are currently making',
        order: 0
    }
};
const getPersonSalaryInfo = (person, jobId) => {
    const jobSalaries = _.orderBy(person.salary.filter((salary) => salary.jobId === jobId), (salary) => salaryKinds[salary.kind].order);
    if (jobSalaries.length === 0) {
        return null;
    }
    const relevantSalaries = _.takeWhile(jobSalaries, (_1, index) => index === 0 || jobSalaries[index - 1].kind === 'volunteered');
    const salaryInfo = relevantSalaries.map((salary) => `${salaryKinds[salary.kind].label} ${salary.value}`).join(';');
    return salaryInfo;
};
const getPersonRecruitingActivityInfo = (person, jobId) => {
    const recruitingActivity = person.recruitingActivity.filter((activity) => activity.jobId === jobId);
    return recruitingActivity && recruitingActivity.length > 0
        ? `${recruitingActivity[recruitingActivity.length - 1].value}`
        : '{Fill in activity}';
};
const roundToHalfYear = (x) => {
    return Math.round(x * 2) / 2; //tslint:disable-line
};
//# sourceMappingURL=interpolate-template.js.map